/**
 * CafePod, 21 Sep 2026. The compare view's one enhancement (CP-043).
 *
 * The table, the sort and the Add buttons are all server-rendered and work
 * with this file missing: each Add is a POST to /cart/add with return_to. This
 * module upgrades that to an in-place add so someone assembling a six-blend
 * basket is not bounced back to the top of the table six times, and it keeps
 * the free-delivery meter honest by re-reading /cart.js on load (the page may
 * be served from cache) and after every add.
 *
 * Why the CLICK is intercepted and not the submit: a third-party storefront
 * script on this store (the page carries Recharge, Klaviyo, Okendo and PushOwl;
 * Recharge's bundle contains exactly this pattern) attaches its own submit
 * handling to forms posting to /cart/add, adds the item itself and sends the
 * shopper to the cart page without checking defaultPrevented. A submit listener
 * here therefore added the pack twice and left the table (found 21 Sep).
 * Cancelling the click in the capture phase means no submit event is ever
 * dispatched, so there is exactly one add. With JavaScript off the form still
 * posts natively and the third-party script is not there either.
 *
 * Meter copy comes from the snippet's data attributes with [placeholders], so
 * the words live in the locale file, not here. GBP only: served territories
 * are UK, Jersey, Guernsey and the Isle of Man.
 */
const meter = document.querySelector('[data-cp-meter]');

const money = (pence) => '£' + (pence / 100).toFixed(2);
const fill = (str, vars) => Object.entries(vars).reduce((s, [k, v]) => s.split('[' + k + ']').join(v), str || '');

function renderMeter(cart) {
  if (!meter || !cart) return;
  const s = meter.dataset;
  const one = Number(s.thresholdOne || 3500);
  const sub = Number(s.thresholdSub || 2500);
  const disc = Number(s.subDiscount || 0.8);
  const total = Number(cart.total_price || 0);
  const count = Number(cart.item_count || 0);
  const hasSub = (cart.items || []).some((i) => i.selling_plan_allocation);

  const line1 = meter.querySelector('[data-cp-meter-line1]');
  const line2 = meter.querySelector('[data-cp-meter-line2]');
  const bar = meter.querySelector('[data-cp-meter-fill]');

  if (line1) line1.textContent = count === 0 ? s.sBasketEmpty : fill(s.sBasketCount, { count }) + ' · ' + money(total);

  let text;
  let pct;
  if (hasSub) {
    const gap = sub - total;
    text = gap <= 0 ? s.sReached : fill(s.sMeterSub, { amount: money(gap) });
    pct = (total / sub) * 100;
  } else {
    const gap = one - total;
    const subGap = sub - Math.round(total * disc);
    if (gap <= 0) text = s.sReached;
    else if (subGap <= 0) text = fill(s.sMeterOnetimeSubReached, { amount: money(gap) });
    else text = fill(s.sMeterOnetime, { amount: money(gap), sub_amount: money(subGap) });
    pct = (total / one) * 100;
  }
  if (line2) line2.textContent = text;
  if (bar) bar.style.width = Math.min(100, Math.max(0, pct)) + '%';
}

async function refresh() {
  try {
    const cart = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());
    renderMeter(cart);
    return cart;
  } catch (e) {
    return null;
  }
}

async function addFrom(form, button) {
  const label = button ? button.textContent : '';
  if (button) button.disabled = true;

  try {
    const data = new FormData(form);
    data.delete('return_to');
    const res = await fetch('/cart/add.js', { method: 'POST', body: data, headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error('add failed ' + res.status);
    const cart = await refresh();

    if (button) {
      button.textContent = button.dataset.cpAddedLabel || 'Added';
      setTimeout(() => {
        button.textContent = label;
        button.disabled = false;
      }, 1600);
    }

    /* Horizon's header cart icon listens for cart:update and reads data.itemCount. */
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart,
          sourceId: form.id || 'cp-compare',
          data: { source: 'cp-compare', itemCount: cart ? cart.item_count : undefined, variantId: String(data.get('id') || '') },
        },
      })
    );
  } catch (e) {
    /* Fall back to the full-page add the form already describes. */
    if (button) button.disabled = false;
    form.submit();
  }
}

document.addEventListener(
  'click',
  (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const button = target.closest('form[data-cp-compare-add] button[type="submit"]');
    if (!button) return;
    const form = button.closest('form');
    if (!form) return;
    event.preventDefault();
    addFrom(form, button);
  },
  { capture: true }
);

refresh();
